import {isEqual} from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProtocolVersion} from 'notebook/ELNConstants';
import getNotePartType from 'notebook/notes/util/getNotePartType';
import {getTableNoteParts} from 'notebook/notes/util/getTableNoteParts';
import {SimpleTableDoc} from 'notebook/simple-tables/useSimpleEvaluation';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableKind';
import {TABLE_OPS} from 'notebook/tables/augmentNotePartTablesWithValues';
import {StructuredTableToTableMetadata, Table} from 'notebook/tables/constants/TableConstants';
import {FullTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {EvaluationSnapshot} from 'notebook/tables/TableStoreTransforms';
import {isNote, isProtocol, isSimpleTableDoc} from 'notebook/TypePredicates';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';
import {getStore} from 'redux-stores/Store';
import {NoteLinkedAssayData} from 'runs/AttachmentConstants';
import APIUtil from 'util/APIUtil';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import {StageRun} from 'workflows/WorkflowConstants';

export function getNoteTableMap(fileId: string, note: Note): FullTableMap {
  const fullTableMap: FullTableMap = {};
  for (const notePart of note.noteParts) {
    // Handle NLG groups and regular table note parts via the shared helper.
    const tableNoteParts = getTableNoteParts(notePart);
    for (const tableNotePart of tableNoteParts) {
      const tableType = getNotePartType(tableNotePart)!;
      // Fetch stage run information for tables in stage entries
      const entryType = APIUtil.apiIdToMaybeType(fileId);
      const isStageEntry =
        entryType === (ModelTypeConstants.STAGE_CONFIGURATION as string) ||
        entryType === (ModelTypeConstants.STAGE_ENTRY as string);
      let stageRuns: Array<StageRun> = [];

      if (isStageEntry) {
        const workflowSelector = new WorkflowSelector(getStore().getState().models.workflows);
        stageRuns = workflowSelector.getStageRunsForEntry(fileId);
      }
      const table = TABLE_OPS[tableType].extractTable(tableNotePart, fileId, stageRuns);
      fullTableMap[table.name] = table;
    }

    // Handle NOTE_LINKED_ASSAY_DATA separately (not a TABLE_OPS type).
    const type = getNotePartType(notePart);
    if (type === ModelTypeConstants.NOTE_LINKED_ASSAY_DATA) {
      if ((notePart.item as NoteLinkedAssayData).versionedData != null) {
        for (const structuredTable of (notePart.item as NoteLinkedAssayData).versionedData.structuredTables) {
          const structuredTableAdapter = getStructuredTableAdapters()[structuredTable.kind];
          const table = structuredTableAdapter.toTable(structuredTable);
          fullTableMap[table.name] = table;
        }
      }
    }
  }
  return fullTableMap;
}

export function getProtocolTableMap(protocol: ProtocolVersion): FullTableMap {
  const fullTableMap: FullTableMap = {};
  protocol.steps.forEach((step) => {
    if (step.table == null) {
      return;
    }
    fullTableMap[step.table.name] = step.table;
  });
  return fullTableMap;
}

/*
 * Validate that the evaluation snapshot and the current note have the same shapes for all the tables
 */
export function isValidEvaluationSnapshot(
  evaluationSnapshot: EvaluationSnapshot,
  item: Note,
  fileId: string,
  fullTableMap: FullTableMap
): boolean {
  for (const [tableName, table] of Object.entries(fullTableMap)) {
    if (!evaluationSnapshot.hasOwnProperty(tableName)) {
      return false;
    }
    const evaluatedTable = evaluationSnapshot[tableName];
    if (evaluatedTable.length !== table.table.length) {
      return false;
    }

    for (let i = 0; i < evaluatedTable.length; i++) {
      if (evaluatedTable[i].length !== table.table[i].length) {
        return false;
      }
    }
  }
  return true;
}

/** The two structured-table kinds that make up a mixture prep group. */
const MIXTURE_PREP_TABLE_KINDS: ReadonlySet<StructuredTableKind> = new Set([
  StructuredTableKind.MIXTURE_INGREDIENTS,
  StructuredTableKind.MIXTURE_METADATA,
]);

/** Returns true if the given table is a mixture prep ingredients or metadata table. */
function isMixturePrepTable(table: Table): boolean {
  const kind =
    table.metadata != null && table.metadata.hasOwnProperty('kind')
      ? (table.metadata as StructuredTableToTableMetadata<unknown>).kind
      : null;
  return kind != null && MIXTURE_PREP_TABLE_KINDS.has(kind);
}

/**
 * Determines whether an invalid evaluation snapshot can be safely repaired by an additive merge that
 * inserts ONLY the missing tables, leaving every existing evaluated table byte-for-byte intact.
 *
 * This is intentionally narrow: it returns true ONLY when the snapshot's sole divergence from the current
 * table map is one or more missing mixture prep tables (ingredients/metadata). This is exactly the state of
 * entries that were evaluated before mixture prep tables participated in entry-level evaluation.
 *
 * It returns false (forcing a normal full re-evaluation) if any of the following hold:
 *   - a non-mixture-prep table is missing from the snapshot,
 *   - any table present in the snapshot has a shape mismatch with the current table.
 */
export function canMergeMissingMixturePrepTables(
  evaluationSnapshot: EvaluationSnapshot,
  fullTableMap: FullTableMap
): boolean {
  let hasMissingMixturePrepTable = false;

  for (const [tableName, table] of Object.entries(fullTableMap)) {
    if (!evaluationSnapshot.hasOwnProperty(tableName)) {
      // A missing table is only mergeable if it is a mixture prep table; anything else is a genuine
      // invalidation that must go through the normal full re-evaluation path.
      if (!isMixturePrepTable(table)) {
        return false;
      }
      hasMissingMixturePrepTable = true;
      continue;
    }
    // A table that is present but mis-shaped is a genuine invalidation, never an additive merge.
    const evaluatedTable = evaluationSnapshot[tableName];
    if (evaluatedTable.length !== table.table.length) {
      return false;
    }
    for (let i = 0; i < evaluatedTable.length; i++) {
      if (evaluatedTable[i].length !== table.table[i].length) {
        return false;
      }
    }
  }

  return hasMissingMixturePrepTable;
}

/**
 * Returns true if the note has a persisted evaluation snapshot that can be repaired by merging in missing
 * mixture prep tables (see {@link canMergeMissingMixturePrepTables}).
 */
export function noteSnapshotCanMergeMissingMixturePrepTables(fileId: string, note: Note): boolean {
  const {evaluationSnapshot} = note.noteRuntimeSnapshot;
  return (
    evaluationSnapshot != null &&
    canMergeMissingMixturePrepTables(evaluationSnapshot, getNoteTableMap(fileId, note))
  );
}

/**
 * Compares evaluationSnapshot dependencies to determine whether a re-evaluation is necessary
 */
export function areEvalSnapshotDepsEqual<ItemType extends Note | ProtocolVersion | SimpleTableDoc>(
  fileId: string,
  oldItem: ItemType | null,
  item: ItemType,
  itemNoteTableMap?: FullTableMap
): boolean {
  if (
    oldItem == null ||
    // For bootstrap case (no evaluation snapshot present), we actually would want to run re-evaluation.
    item.noteRuntimeSnapshot.evaluationSnapshot == null ||
    // We check shallow equality since the evaluationSnapshots should point to the same underlying object if identical
    item.noteRuntimeSnapshot !== oldItem.noteRuntimeSnapshot ||
    item.selectors !== oldItem.selectors
  ) {
    return false;
  }

  // Since we build a new table map for each evaluation snapshot, we compare using deep equality.
  if (isNote(item) && isNote(oldItem)) {
    if (itemNoteTableMap == null) {
      itemNoteTableMap = getNoteTableMap(fileId, item);
    }
    return isEqual(getNoteTableMap(fileId, oldItem), itemNoteTableMap);
  } else if (isProtocol(item) && isProtocol(oldItem)) {
    return isEqual(getProtocolTableMap(item), getProtocolTableMap(oldItem));
  } else if (isSimpleTableDoc(item) && isSimpleTableDoc(oldItem)) {
    return isEqual(item.fullTableMap, oldItem.fullTableMap);
  }
  return false;
}

export function tableProvenanceUuidsForNotePart(notePart: NotePart): Array<string> {
  const type = getNotePartType(notePart);
  if (!type || !TABLE_OPS[type]) {
    return [];
  }
  return TABLE_OPS[type].provenanceUuids(notePart);
}
